<template>
  <component
    :is="tag"
    :contenteditable="contenteditable"
    @input="update"
    @blur="onBlur"
    @paste="onPaste"
    @keypress="onKeypress"
    @keydown="onKeydown"
    @focus="onFocus"
    ref="element"
  >
  </component>
</template>

<script>
import { ref, onMounted, watch, watchEffect, nextTick } from 'vue'
import debounce from 'lodash/debounce'

function replaceAll(str, search, replacement) {
  return str.split(search).join(replacement)
}

export default {
  name: 'contenteditable',
  props: {
    tag: String,
    contenteditable: {
      type: Boolean,
      default: true
    },
    modelValue: [ String ],
    noHTML: {
      type: Boolean,
      default: true
    },
    noNL: {
      type: Boolean,
      default: false
    },
    alwaysSelectAll: {
      type: [Boolean, String],
      default: false
    },
    autoFocus: Boolean,
    onlyNumbers: {
      type: Boolean,
      default: false
    },
  },
  emits: {
    returned: String,
    'update:modelValue': [ String, null, undefined],
    updated: Boolean,
    focused: Boolean
  },
  setup(props, { emit }) {
    const element = ref(null)
    let lastContent = props.modelValue // eslint-disable-line
    let focused = false

    function currentContent() {
      return props.noHTML ? element.value?.textContent : element.value?.innerHTML
    }
    
    function updateContent(newcontent) {
      if (!element.value) return
      if (props.noHTML) {
        element.value.textContent = newcontent
      } else {
        element.value.innerHTML = newcontent
      }
    }
    
    function update() {
      emit('update:modelValue', currentContent())
    }
    
    function updated() {
      const curContent = currentContent()
      if (lastContent !== curContent) {
        update()
        emit('updated', true)
        lastContent = curContent
      }
    }
    
    function onPaste(event) {
      event.preventDefault()
      let text = (event.originalEvent || event).clipboardData.getData(
        'text/plain'
      )
      if (props.noNL) {
        text = replaceAll(text, '\r\n', ' ')
        text = replaceAll(text, '\n', ' ')
        text = replaceAll(text, '\r', ' ')
      }

      if (props.onlyNumbers) {
        text = text.replace(/[^0-9]+/g, '')
        // todo ,. in text?
      }
      window.document.execCommand('insertText', false, text)
    }
    
    const keypressUpdate = debounce(() => {
      updated()
    }, 1000)

    function onKeypress(event) {
      if (props.onlyNumbers) {
        if (!(
          event.code.includes('Digit')
          || event.code.includes('Numpad')
          || event.code === 'ArrowLeft'
          || event.code === 'ArrowRight'
          || event.code === 'Backspace'
          || event.code === 'Delete'
          || event.code === 'Period'
          || event.code === 'Comma'
        ) || ((currentContent().includes('.') || currentContent().includes(',')) && (event.code === 'Comma' || event.code === 'Period'))) {
          event.preventDefault()
        }
      }

      if (event.key == 'Enter' && props.noNL) {
        event.preventDefault()
        emit('returned', currentContent())
      }
      keypressUpdate()
    }
    
    function onKeydown(event) {
      if ((event.ctrlKey || event.metaKey) && props.noHTML) {
        switch (event.key) {
          case 'c':
          case 'C':
          case 'x':
          case 'X':
          case 'v':
          case 'V':
          case 'a':
          case 'A':
          case 'ArrowRight':
          case 'ArrowLeft':
            return true

          default:
            event.preventDefault()
            return false
        }
      }
    }
    
    function onFocus() {
      focused = true
      if (
        props.alwaysSelectAll === true ||
        props.alwaysSelectAll === currentContent()
      ) {
        setTimeout(() => {
          let sel, range
          if (window.getSelection && document.createRange) {
            range = document.createRange()
            range.selectNodeContents(element.value)
            sel = window.getSelection()
            sel.removeAllRanges()
            sel.addRange(range)
          } else if (document.body.createTextRange) {
            range = document.body.createTextRange()
            range.moveToElementText(element.value)
            range.select()
          }
        }, 10)
      }

      emit('focused', true)
    }

    function onBlur() {
      updated()
      focused = false
      nextTick(() => {
        updateContent(props.modelValue ?? '')
      })
    }
    
    onMounted(() => {
      updateContent(props.modelValue ?? '')
      if (props.autoFocus) {
        onFocus()
      }
    })
    
    watch(
      () => props.modelValue,
      (newval) => {
        if (newval != currentContent() && focused === false) {
          updateContent(newval ?? '')
        }
      }
    )

    watchEffect(() => {
      if (props.autoFocus && element.value) {
        element.value.focus()
      }
    },
    {
      flush: 'post'
    })

    return {
      update,
      updated,
      onPaste,
      onKeypress,
      onKeydown,
      element,
      onFocus,
      onBlur
    }
  }
}
</script>

<style lang="scss">
[contenteditable='true'] {
  @apply block;
}
</style>
